import {VolumeMode} from '@cypro/engine/database/schema'
import {PrescribedName} from '@cypro/engine/schema'
import {calculatePreparationConcentration, type PreparationDrugData} from '@cypro/engine/utils'
import {getNumberFormatter, multipleDigitsNumberFormatter} from '@cypro/ui/utils'
import {TFnType} from '@tolgee/react'

export interface PreparationConcentrationDisplay {
  /** e.g. "0.48 mg/ml" — "≈ " prefixed when approximate */
  concentration: string
  /** e.g. "500 ml total" */
  volumeTotal: string
  /** e.g. "240 mg in 500 ml" plus the approximate note when it applies */
  calculation: string
  /** e.g. "500 ml" */
  finalVolume: string
  approximate: boolean
}

/** structural view of a prescribed-name product used by the pop-up and the drawer */
export interface MedicineWithTradeNameSource {
  prescribedName?: PrescribedName
}

/**
 * trade names carrying 0 or several strength rows make the product mix unknowable —
 * the concentration preview then falls back to carrier-only (approximate in IN mode)
 */
export function getTradeNameDrugData(medicine?: MedicineWithTradeNameSource | null): PreparationDrugData | undefined {
  const tradeNames = (medicine?.prescribedName?.tradeNames ?? []).filter(tradeName => !tradeName.deletedAt)
  if (tradeNames.length !== 1) return undefined

  const tradeName = tradeNames[0]
  return {
    name: tradeName.name,
    isSolid: Boolean(tradeName.medicationProfile?.isDrySubstance || tradeName.reconstitutionRequired),
    strengthValue: tradeName.strengthValue,
    strengthUnitUcum: tradeName.strengthUnit?.ucumCode,
    volumeValue: tradeName.volumeValue,
    density: tradeName.density ?? undefined,
    reconstitutionRequired: tradeName.reconstitutionRequired,
    reconstitutedSolutionConcentration: tradeName.reconstitutedSolutionConcentration,
    reconstitutedConcentrationUnitUcum: tradeName.reconstitutedConcentrationUnit?.ucumCode,
    reconstitutedSolutionVolumeExpansion: tradeName.reconstitutedSolutionVolumeExpansion,
    solvents: tradeName.reconstitutionSolvents?.map(solvent => ({
      name: solvent.solutionDrug?.name,
      volumeMl: solvent.solventVolume,
    })),
  }
}

const CONCENTRATION_FORMATTER = getNumberFormatter({minimumFractionDigits: 2, maximumFractionDigits: 2})

/**
 * Read-only final-concentration display for the prescription pop-up and the
 * side drawer — both call this so the next consumer (e.g. a printed label)
 * shows the same value.
 */
export function getPreparationConcentrationDisplay(
  input: {
    medicines?: readonly (
      | {
          totalAmount?: number | null
          doseUnitUcum?: string | null
          drug?: PreparationDrugData | null
        }
      | undefined
    )[]
    carrierVolumeMl?: number | null
    volumeMode?: VolumeMode | null
  },
  t: TFnType
): PreparationConcentrationDisplay | undefined {
  const medicines = (input.medicines ?? []).filter((medicine): medicine is NonNullable<typeof medicine> =>
    Boolean(medicine)
  )

  const result = calculatePreparationConcentration({
    carrierVolumeMl: input.carrierVolumeMl,
    volumeMode: input.volumeMode,
    substances: medicines.map(medicine => ({totalAmount: medicine.totalAmount, drug: medicine.drug})),
  })
  if (!result) return undefined

  const doseUnitUcum = medicines.find(medicine => medicine.doseUnitUcum)?.doseUnitUcum || ''
  const totalAmount = medicines.reduce((acc, medicine) => acc + (medicine.totalAmount ?? 0), 0)
  const concentration = `${result.approximate ? '≈ ' : ''}${CONCENTRATION_FORMATTER.format(
    result.concentrationPerMl
  )} ${doseUnitUcum}/ml`
  const calculation =
    t('therapy_sequence.concentration.dose_in_volume', {
      dose: multipleDigitsNumberFormatter.format(totalAmount),
      unit: doseUnitUcum,
      volume: multipleDigitsNumberFormatter.format(result.finalVolumeMl),
    }) + (result.approximate ? ` · ${t('therapy_sequence.concentration.approximate')}` : '')
  const finalVolume = `${multipleDigitsNumberFormatter.format(result.finalVolumeMl)} ml`
  const volumeTotal = t('therapy_sequence.concentration.volume_total', {
    volume: multipleDigitsNumberFormatter.format(result.finalVolumeMl),
  })

  return {concentration, volumeTotal, calculation, finalVolume, approximate: result.approximate}
}
